import { createSyncConnection } from './flowdeskDataModel.js';

const normalizeEndpoint = (endpointUrl) => (
  String(endpointUrl || '').trim().replace(/\/+$/, '')
);

const getErrorMessage = (error, fallback = 'Unknown network error') => {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object') {
    if (typeof error.message === 'string') return error.message;
    if (error.error) return getErrorMessage(error.error, fallback);
    if (error.code || error.message) {
      return [error.code, getErrorMessage(error.message, '')].filter(Boolean).join(': ');
    }
    try {
      return JSON.stringify(error);
    } catch {
      return fallback;
    }
  }
  return String(error);
};

const getServerErrorMessage = (payload, fallback) => (
  getErrorMessage(payload?.error ?? payload?.message, fallback)
);

export const createHttpSyncTransport = ({ connection, fetchRef = fetch } = {}) => {
  const syncConnection = createSyncConnection(connection || {});
  const endpointUrl = normalizeEndpoint(syncConnection.endpointUrl);

  return {
    name: 'http',
    isConfigured: Boolean(endpointUrl),
    fetchChanges: async (afterRevision = 0) => {
      if (!endpointUrl) {
        return {
          ok: false,
          error: 'No sync endpoint is configured.'
        };
      }

      try {
        const response = await fetchRef(`${endpointUrl}/sync/changes?afterRevision=${Number(afterRevision || 0)}`, {
          method: 'GET',
          headers: {
            ...(syncConnection.accessToken ? { Authorization: `Bearer ${syncConnection.accessToken}` } : {})
          }
        });
        const payload = await response.json().catch(() => null);

        if (!response.ok) {
          return {
            ok: false,
            status: response.status,
            error: getServerErrorMessage(payload, `Sync endpoint returned ${response.status}.`)
          };
        }

        return {
          ok: true,
          status: response.status,
          revision: Number(payload?.data?.revision || afterRevision || 0),
          changes: Array.isArray(payload?.data?.changes) ? payload.data.changes : []
        };
      } catch (error) {
        return {
          ok: false,
          error: getErrorMessage(error)
        };
      }
    },
    send: async (operation) => {
      if (!endpointUrl) {
        return {
          ok: false,
          operationId: operation.id,
          error: 'No sync endpoint is configured.'
        };
      }

      try {
        const response = await fetchRef(`${endpointUrl}/sync/operations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(syncConnection.accessToken ? { Authorization: `Bearer ${syncConnection.accessToken}` } : {})
          },
          body: JSON.stringify({ operation })
        });

        const payload = await response.json().catch(() => null);

        if (!response.ok) {
          return {
            ok: false,
            operationId: operation.id,
            status: response.status,
            error: getServerErrorMessage(payload, `Sync endpoint returned ${response.status}.`)
          };
        }

        return {
          ok: true,
          operationId: operation.id,
          status: response.status,
          payload
        };
      } catch (error) {
        return {
          ok: false,
          operationId: operation.id,
          error: getErrorMessage(error)
        };
      }
    }
  };
};

export const createConfiguredSyncTransport = ({ connection } = {}) => {
  const syncConnection = createSyncConnection(connection || {});
  if (!syncConnection.enabled || !syncConnection.endpointUrl.trim()) return null;
  return createHttpSyncTransport({ connection: syncConnection });
};
